import React from "react"
import { observer } from "mobx-react"
import SVG from "react-inlinesvg"
import checkmarkSvg from "../../../../priv/static/svg/generic/checkmark-yes.svg"

interface Props {
  status: string
  isTripLeader: boolean
  tripLeaderPermission: "full" | "assign_only" | "read_only"
  updatePath: string
  reviewNote: string
}

const Button = ({ buttonText, url, isLocked, extraClasses }) => {
  let classNames = `legacy-with-icon svg-icon button fg-gray-50 ${extraClasses}`

  if (isLocked) {
    return (
      <span className={classNames}>
        <SVG src={checkmarkSvg} className="" />
        Locked
      </span>
    )
  } else {
    return (
      <a className={classNames} href={url} data-role="status-link">
        <SVG src={checkmarkSvg} className="" />
        {buttonText}
      </a>
    )
  }
}

Button.defaultProps = {
  extraClasses: "",
}

const StatusBox = ({ statusText, explanationText, children }) => {
  return (
    <div data-role="list-status" className="stack grow-1 split-last gap-4 pad-6 | border-gray-200 bg-gray-50">
      <h3 className="type-heading-sm">{statusText}</h3>
      <p className="type-body-md">{explanationText}</p>
      {children}
    </div>
  )
}

@observer
export class TripLeaderStatusBox extends React.Component<Props> {
  constructor(props) {
    super(props)
  }

  buttonText = () => {
    let statuses = {
      draft: "Request Approval",
      pending: "Pending Approval",
      approved: "Approved",
      rejected: "Request Approval",
    }

    return statuses[this.props.status]
  }

  explanationText = () => {
    let { status, tripLeaderPermission } = this.props
    if (tripLeaderPermission === "read_only") {
      return "The tour operator has locked this list from any further edits. Please reach out if you need to have any edits made."
    }
    let texts = {
      draft: "The list is currently a draft.",
      pending: "The list is currently under review by tour operator",
      approved: "The list has been approved by the tour operator",
      rejected: `The tour operator has rejected the list.`,
    }
    return texts[status]
  }

  statusText = () => {
    let statuses = {
      draft: "Draft",
      pending: "Approval Requested",
      approved: "Approved",
      rejected: "Draft",
    }

    return statuses[this.props.status]
  }

  showButton = () => {
    if (this.props.tripLeaderPermission === "read_only") return false
    return ["draft", "rejected"].includes(this.props.status)
  }

  render() {
    let { reviewNote, updatePath, isTripLeader, tripLeaderPermission } = this.props

    return (
      <StatusBox {...this.props} statusText={this.statusText()} explanationText={this.explanationText()}>
        {reviewNote && <p className="type-body-md"> Review notes: {reviewNote}</p>}
        {this.showButton() ? (
          <div>
            <Button
              url={updatePath}
              isLocked={isTripLeader && tripLeaderPermission === "read_only"}
              buttonText={this.buttonText()}
            />
          </div>
        ) : (
          <div className="divider"></div>
        )}
      </StatusBox>
    )
  }
}

@observer
export class TourOperatorStatusBox extends React.Component<Props> {
  constructor(props) {
    super(props)
  }

  statusText = () => {
    let statuses = {
      draft: "Draft",
      pending: "Approval Requested",
      approved: "Approved",
      rejected: "Draft",
    }

    return statuses[this.props.status]
  }

  buttonText = () => {
    let statuses = {
      draft: "Approve",
      pending: "Approval Requested",
      approved: "Approved",
      rejected: "Approve",
    }

    return statuses[this.props.status]
  }

  explanationText = () => {
    let { status, tripLeaderPermission } = this.props
    if (tripLeaderPermission === "read_only") {
      return "List has been locked, Trip Leaders can no longer make edits"
    }
    let texts = {
      draft: "The list is currently a draft.",
      pending:
        "A trip leader has requested your approval for this list. Once approved, you have the option of locking down further edits.",
      approved: "The list has been approved by the tour operator",
      rejected: "The list has been rejected.",
    }
    return texts[status]
  }

  render() {
    let { reviewNote, updatePath, isTripLeader, tripLeaderPermission } = this.props

    return (
      <StatusBox {...this.props} statusText={this.statusText()} explanationText={this.explanationText()}>
        {reviewNote && <p className="max-inline-125 | type-body-md"> Review notes: {reviewNote}</p>}
        {
          <Button
            url={updatePath}
            isLocked={isTripLeader && tripLeaderPermission === "read_only"}
            buttonText={this.buttonText()}
          />
        }
      </StatusBox>
    )
  }
}
